import { Company } from 'module/api/company/types';
import { EmptyState } from 'module/ui/components/atoms/empty-state';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@rocketlane/ui';
import { Skeleton } from 'antd';
import { AccountPeopleProfile } from 'api/account-people/types';
import moment from 'moment';
import { useGetPaginatedSignalsQuery } from 'queries/all-signals';
import { useRef, useEffect, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { WidthTransition } from 'styles/common';
import { useGetReasonField } from 'components/organisms/fields/utils';
import { SignalOutputCard } from 'components/organisms/signal-output';
import { SignalSelector } from 'components/pages/accounts/detail/common/signal-selector';
import { useFilterForSignalsFetch } from './use-filter';
import { getIcon, Sections } from '../../content-navigator';
import { ContentSection, EmptyStateForSection } from '../../content-section';

interface Props {
  company: Company;
  userProfile: AccountPeopleProfile;
  isFullWidth?: boolean;
}
const SignalList = styled.div<{ $isFullWidth?: boolean }>`
  ${WidthTransition};
  ${({ $isFullWidth }) =>
    $isFullWidth
      ? css`
          max-width: 100%;
        `
      : css`
          max-width: 560px;
        `};
`;

export const SignalSection = ({ company, userProfile, isFullWidth }: Props) => {
  const [signalConfigurationIds, setSignalConfigurationIds] = useState<
    number[]
  >([]);
  const { filter, loading: isFilterDefinitionLoading } =
    useFilterForSignalsFetch({
      userProfile,
      signalConfigurationIds,
    });
  const reasonField = useGetReasonField();

  const {
    data: paginatedSignals,
    isLoading: isPaginatedSignalsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useGetPaginatedSignalsQuery({
    filter: filter,
    companyId: company.companyId,
  });
  const isEmpty = (paginatedSignals ?? []).length === 0;
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          if (!hasNextPage || isFetchingNextPage || isPaginatedSignalsLoading) {
            return;
          }
          fetchNextPage();
        }
      },
      { root: null, rootMargin: '0px', threshold: 0 },
    );
    const node = loadMoreRef.current;

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isPaginatedSignalsLoading,
  ]);
  const signalsRange = useMemo(() => {
    if (
      userProfile.firstSignalOccurredAt == null ||
      userProfile.recentSignalOccurredAt == null
    ) {
      return undefined;
    }

    return `${moment(userProfile.firstSignalOccurredAt).format('MMM YYYY')} - ${moment(userProfile.recentSignalOccurredAt).format('MMM YYYY')}`;
  }, [userProfile.firstSignalOccurredAt, userProfile.recentSignalOccurredAt]);

  return (
    <>
      <ContentSection
        icon={<FontAwesomeIcon icon={getIcon(Sections.Signals)} size={16} />}
        title={
          <FormattedMessage id="account.people.profile.sections.signals.title" />
        }
        description={signalsRange}
        sectionKey={Sections.Signals}
        headerActions={
          <CenterAlignedBox>
            {signalConfigurationIds.length > 0 && (
              <Button
                variant="ghost"
                leftIcon={<FontAwesomeIcon icon={faClose} size="lg" />}
                onClick={() => {
                  setSignalConfigurationIds([]);
                }}
              />
            )}
            <SignalSelector
              value={signalConfigurationIds}
              onChange={setSignalConfigurationIds}
            />
          </CenterAlignedBox>
        }
        loading={isPaginatedSignalsLoading || isFilterDefinitionLoading}
      >
        {isEmpty ? (
          <EmptyStateForSection
            sectionKey={Sections.Signals}
            message={
              signalConfigurationIds.length > 0 ? (
                <FormattedMessage id="account.people.profile.sections.signals.empty_for_filter" />
              ) : (
                <FormattedMessage id="account.people.profile.sections.signals.empty" />
              )
            }
            action={
              signalConfigurationIds.length > 0 ? (
                <EmptyState.CallToAction.Primary
                  onClick={() => {
                    setSignalConfigurationIds([]);
                  }}
                >
                  <FormattedMessage id="reset" />
                </EmptyState.CallToAction.Primary>
              ) : null
            }
          />
        ) : (
          <SignalList $isFullWidth={isFullWidth}>
            {paginatedSignals?.map((signal) => {
              const reason = signal.fields?.find((field) => {
                return field.fieldId === reasonField?.fieldId;
              });

              return (
                <SignalOutputCard
                  key={signal?.signalOccurrenceId}
                  sourceId={signal?.sourceId}
                  sourceType={signal?.sourceType}
                  people={signal?.reportedBy ?? []}
                  reason={reason}
                  title={signal?.title}
                  description={signal?.summary}
                  tasks={[]}
                  insightfulContent={''}
                  type={signal?.signalName}
                  signalType={signal?.signalType}
                  className="timeline-details-signal"
                  citations={signal?.citationMapping}
                />
              );
            })}

            {isFetchingNextPage && <Skeleton active />}
          </SignalList>
        )}
      </ContentSection>
      <div ref={loadMoreRef} style={{ height: 10 }} />
    </>
  );
};
